import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from 'src/services/api.config';
import FlowCustomContent, {
  type FlowDesignTranslations,
} from 'src/content/pages/FlowCustom/FlowCustomContent';
import {
  appendFlowDesignFormData,
  defaultFlowDesignForm,
  type FlowDesignForm,
} from '../flow-design.form';
import {
  detectProductsFromCheckOptions,
  filterDocsForProduct,
  isIdvCheckOption,
  isPoaCheckOption,
  isCardCheckOption,
  normalizeCheckOptions,
} from '../check-options';
import type { ProductId } from '../new-flow-wizard.state';
import {
  defaultCheckConfig,
  sanitizeServiceOptions,
  type CheckConfig,
  type NotificationConfig,
} from '../flow-builder/types';
import { ProductConfig } from '../flow-builder/product-config';
import { Field, Panel, Select, ToggleRow } from '../flow-builder/ui';
import {
  ScanFace,
  FileText,
  Landmark,
  Wallet,
  ShieldAlert,
  CreditCard,
  Check,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  X,
  GripVertical,
  ArrowRight,
  Palette,
  Settings2,
  Lock,
} from 'lucide-react';

export type { NewFlowWizardState, ProductId } from '../new-flow-wizard.state';

const SONIO_DEMO_CUSTOMER_ID = '649025e334a855cf37cf58c7';

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepMergePlain(
  base: Record<string, unknown> | undefined,
  patch: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!patch) return { ...(base ?? {}) };
  const out: Record<string, unknown> = { ...(base ?? {}) };
  for (const key of Object.keys(patch)) {
    const pv = patch[key];
    const bv = out[key];
    if (isPlainRecord(pv) && isPlainRecord(bv)) {
      out[key] = deepMergePlain(bv, pv);
    } else if (pv !== undefined) {
      out[key] = pv;
    }
  }
  return out;
}

type Product = {
  id: ProductId;
  name: string;
  short: string;
  description: string;
  icon: typeof ScanFace;
  duration: string;
};

const PRODUCTS: Product[] = [
  {
    id: 'idv',
    name: 'Identity Verification',
    short: 'IDV',
    description:
      'Government-issued document capture with biometric face match and liveness.',
    icon: ScanFace,
    duration: '~30s',
  },
  {
    id: 'poa',
    name: 'Proof of Address',
    short: 'POA',
    description:
      'Utility bills or bank statements analyzed against residency data.',
    icon: FileText,
    duration: '~20s',
  },
  {
    id: 'bank',
    name: 'Bank Verification',
    short: 'Bank',
    description:
      'Confirm account ownership and IBAN via Open Banking connections.',
    icon: Landmark,
    duration: '~40s',
  },
  {
    id: 'affordability',
    name: 'Affordability',
    short: 'Afford.',
    description:
      "Income and expense analysis to assess a customer's financial capacity.",
    icon: Wallet,
    duration: '~25s',
  },
  {
    id: 'aml',
    name: 'AML Check',
    short: 'AML',
    description:
      'Screen against PEPs, sanctions, and adverse-media watchlists.',
    icon: ShieldAlert,
    duration: '~5s',
  },
  {
    id: 'card',
    name: 'Credit Card Verification',
    short: 'Card',
    description:
      'Confirms that the person using a card is the authorized cardholder and verifies that the card is active',
    icon: CreditCard,
    duration: '~15s',
  },
];

const STEPS = [
  { n: 1, label: 'Select products' },
  { n: 2, label: 'Rules & validation' },
  { n: 3, label: 'UI customization' },
] as const;

function toHex(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (/^#[0-9A-Fa-f]{3,8}$/.test(s)) return s;
  if (/^[0-9A-Fa-f]{3,8}$/.test(s)) return `#${s}`;
  return undefined;
}

function extractBgHex(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const m = v.match(/bg-\[(#[0-9A-Fa-f]{3,8})\]/i);
  return m?.[1];
}

function getBannerUrl(bannerImage: unknown): string | null {
  if (!Array.isArray(bannerImage)) return null;
  for (const entry of bannerImage) {
    if (!Array.isArray(entry)) continue;
    const banner = entry.find(
      (kv: { Key?: string }) => kv?.Key === 'banner',
    ) as { Value?: Array<{ Key?: string; Value?: string }> } | undefined;
    const en = banner?.Value?.find(x => x?.Key === 'en')?.Value;
    const any = banner?.Value?.find(x => typeof x?.Value === 'string')?.Value;
    const url = en || any;
    if (url) return url;
  }
  return null;
}

function buildCheckOptionsFromConfigs(
  selected: ProductId[],
  configs: Partial<Record<ProductId, CheckConfig>>,
): string[] {
  const options: string[] = [];
  for (const id of selected) {
    const cfg = configs[id] ?? defaultCheckConfig(id);
    for (const opt of cfg.checkoptions) {
      const documents = filterDocsForProduct(opt.documents, id);
      if (!documents.length) continue;
      const providers =
        id === 'idv'
          ? ['incode']
          : id === 'poa'
            ? ['incode', 'incode+sonio']
            : opt.providers;
      if (!providers.length) continue;
      options.push(
        `document:${documents.join(',')}:${providers.join(',')}`,
      );
    }
  }
  return options;
}

/** UI product id → API dataMatch key (idv is stored as poi). */
const DATAMATCH_API_KEY: Partial<Record<ProductId, string>> = {
  idv: 'poi',
  poa: 'poa',
  bank: 'bank',
  affordability: 'affordability',
  aml: 'aml',
};

const DATAMATCH_PRODUCT_FROM_API: Record<string, ProductId> = {
  poi: 'idv',
  poa: 'poa',
  bank: 'bank',
  affordability: 'affordability',
  aml: 'aml',
};

export type DataMatchProductPayload = {
  datamatchlimits: { fail: number; review: number };
  datamatch: Record<
    string,
    {
      active: boolean;
      service: string[];
      limits: { fail: number; review: number };
    }
  >;
};

export type DataMatchPayload = Record<string, DataMatchProductPayload>;

function buildDataMatchPayload(
  selected: ProductId[],
  configs: Partial<Record<ProductId, CheckConfig>>,
): DataMatchPayload {
  const payload: DataMatchPayload = {};

  for (const id of selected) {
    const apiKey = DATAMATCH_API_KEY[id];
    if (!apiKey) continue;

    const cfg = configs[id] ?? defaultCheckConfig(id);
    const datamatch: DataMatchProductPayload['datamatch'] = {};

    for (const [fieldKey, rule] of Object.entries(cfg.settings.datamatch)) {
      if (!rule?.active) continue;
      datamatch[fieldKey] = {
        active: true,
        service: sanitizeServiceOptions(
          Array.isArray(rule.service) ? rule.service : [],
        ),
        limits: {
          fail: rule.limits?.fail ?? 0,
          review: rule.limits?.review ?? 75,
        },
      };
    }

    // Skip products with no active match rules and default limits unused
    if (Object.keys(datamatch).length === 0) continue;

    payload[apiKey] = {
      datamatchlimits: {
        fail: cfg.settings.datamatchlimits?.fail ?? 0,
        review: cfg.settings.datamatchlimits?.review ?? 75,
      },
      datamatch,
    };
  }

  return payload;
}

function applyDataMatchToConfigs(
  configs: Partial<Record<ProductId, CheckConfig>>,
  dataMatch: unknown,
): Partial<Record<ProductId, CheckConfig>> {
  if (!dataMatch || typeof dataMatch !== 'object') return configs;

  const next = { ...configs };
  for (const [apiKey, section] of Object.entries(
    dataMatch as Record<string, unknown>,
  )) {
    const productId = DATAMATCH_PRODUCT_FROM_API[apiKey];
    if (!productId || !section || typeof section !== 'object') continue;

    const sectionObj = section as {
      datamatchlimits?: { fail?: number; review?: number };
      datamatch?: Record<
        string,
        {
          active?: boolean;
          service?: string[];
          limits?: { fail?: number; review?: number };
        }
      >;
    };

    const base = next[productId] ?? defaultCheckConfig(productId);
    const datamatch = { ...base.settings.datamatch };

    if (sectionObj.datamatch && typeof sectionObj.datamatch === 'object') {
      for (const [fieldKey, rule] of Object.entries(sectionObj.datamatch)) {
        if (!rule || typeof rule !== 'object') continue;
        datamatch[fieldKey] = {
          active: Boolean(rule.active),
          service: sanitizeServiceOptions(
            Array.isArray(rule.service) ? rule.service : [],
          ),
          limits: {
            fail: rule.limits?.fail ?? datamatch[fieldKey]?.limits.fail ?? 0,
            review:
              rule.limits?.review ?? datamatch[fieldKey]?.limits.review ?? 75,
          },
        };
      }
    }

    next[productId] = {
      ...base,
      settings: {
        ...base.settings,
        datamatchlimits: {
          fail:
            sectionObj.datamatchlimits?.fail ??
            base.settings.datamatchlimits.fail,
          review:
            sectionObj.datamatchlimits?.review ??
            base.settings.datamatchlimits.review,
        },
        datamatch,
      },
    };
  }

  return next;
}

function hydrateConfigsFromCheckOptions(
  checkOptions: unknown,
  products: ProductId[],
): Partial<Record<ProductId, CheckConfig>> {
  const opts = normalizeCheckOptions(checkOptions);
  const next: Partial<Record<ProductId, CheckConfig>> = {};

  for (const id of products) {
    const cfg = defaultCheckConfig(id);
    const matching = opts.filter(opt => {
      if (id === 'poa') return isPoaCheckOption(opt);
      if (id === 'idv') return isIdvCheckOption(opt);
      if (id === 'card') return isCardCheckOption(opt);
      return false;
    });

    if (matching.length) {
      const branches = matching
        .map(raw => {
          const parts = raw.split(':');
          const docs = filterDocsForProduct(
            (parts[1] || '')
              .split(',')
              .map(d => d.trim())
              .filter(Boolean),
            id,
          );
          const providers =
            id === 'idv'
              ? ['incode']
              : id === 'poa'
                ? ['incode', 'incode+sonio']
                : (parts[2] || '')
                    .split(',')
                    .map(p => p.trim())
                    .filter(Boolean);
          return { documents: docs, providers };
        })
        .filter(branch => branch.documents.length > 0);

      // Merge duplicate branches that only differ after filtering
      const merged = new Map<string, { documents: string[]; providers: string[] }>();
      for (const branch of branches) {
        const key = [...branch.documents].sort().join(',');
        const existing = merged.get(key);
        if (existing) {
          existing.providers = [
            ...new Set([...existing.providers, ...branch.providers]),
          ];
        } else {
          merged.set(key, {
            documents: branch.documents,
            providers: [...branch.providers],
          });
        }
      }
      cfg.checkoptions = [...merged.values()];
    }
    next[id] = cfg;
  }

  return next;
}

export default function NewFlowWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editFlowId = searchParams.get('flowId') ?? '';
  const isEditMode = editFlowId.trim().length > 0;

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [flowName, setFlowName] = useState('');
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [customCustomerId, setCustomCustomerId] = useState('');
  const customerId =
    useCustomCustomer && customCustomerId.trim()
      ? customCustomerId.trim()
      : SONIO_DEMO_CUSTOMER_ID;
  const [incodeClientId, setIncodeClientId] = useState('');
  const [apiVersion, setApiVersion] = useState('4');
  const [googleAddress, setGoogleAddress] = useState(false);
  const [notification, setNotification] = useState<NotificationConfig>({
    url: 'skip',
    authtype: 'basic',
    username: '',
    password: '',
    send400: true,
    retryon500: true,
  });
  const [selected, setSelected] = useState<ProductId[]>(() =>
    isEditMode ? [] : ['idv', 'aml'],
  );
  const [expanded, setExpanded] = useState<ProductId | null>(() =>
    isEditMode ? null : 'idv',
  );
  const [configs, setConfigs] = useState<
    Partial<Record<ProductId, CheckConfig>>
  >({});
  const [flowDesign, setFlowDesign] = useState<FlowDesignForm>(
    defaultFlowDesignForm,
  );

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [loadFlowError, setLoadFlowError] = useState<string | null>(null);

  useEffect(() => {
    if (!editFlowId) return;
    let cancelled = false;
    setLoadingFlow(true);
    setLoadFlowError(null);

    axios
      .get(`${API_BASE_URL}/flows/${encodeURIComponent(editFlowId)}`)
      .then(response => {
        if (cancelled) return;
        const data = response?.data ?? {};

        setFlowName(data.flowName ?? data.name ?? data.layoutName ?? '');
        const loadedCustomerId = String(
          data.customerId ?? data.customerid ?? SONIO_DEMO_CUSTOMER_ID,
        ).trim();
        if (loadedCustomerId && loadedCustomerId !== SONIO_DEMO_CUSTOMER_ID) {
          setUseCustomCustomer(true);
          setCustomCustomerId(loadedCustomerId);
        } else {
          setUseCustomCustomer(false);
          setCustomCustomerId('');
        }
        setIncodeClientId(
          data.incodeClientId ??
            data.externalIncodeId ??
            data.externalids?.incode ??
            '',
        );

        const detectedProducts = detectProductsFromCheckOptions(
          data.checkOptions,
        );
        if (detectedProducts.length > 0) {
          setSelected(detectedProducts);
          setExpanded(detectedProducts[0]);
          const fromChecks = hydrateConfigsFromCheckOptions(
            data.checkOptions,
            detectedProducts,
          );
          setConfigs(
            applyDataMatchToConfigs(
              fromChecks,
              data.dataMatch ?? data.datamatch,
            ),
          );
        } else if (data.dataMatch || data.datamatch) {
          setConfigs(
            applyDataMatchToConfigs({}, data.dataMatch ?? data.datamatch),
          );
        }

        const flat: Record<string, any> = data?.design ?? data;
        const themeColor = flat?.theme?.color ?? flat?.color ?? undefined;
        const fromFlat = flat?.translations;
        const fromRoot = data?.translations;
        const rawTranslations =
          typeof fromFlat === 'object' && fromFlat !== null
            ? fromFlat
            : typeof fromRoot === 'object' && fromRoot !== null
              ? fromRoot
              : undefined;

        const nextDesign: FlowDesignForm = {
          appBarLogo:
            typeof flat?.appBarLogo === 'string'
              ? flat.appBarLogo
              : typeof flat?.appBar?.appBarLogo === 'string'
                ? flat.appBar.appBarLogo
                : undefined,
          appBarLogoFile: null,
          bannerImg:
            getBannerUrl(flat?.bannerImage ?? flat?.theme?.bannerImage) ?? null,
          bannerImageFile: null,
          bannerRemoved: false,
          appBarClass:
            toHex(flat?.appBarClass) ??
            extractBgHex(flat?.appBarClass) ??
            extractBgHex(flat?.appBar?.appBarClass) ??
            defaultFlowDesignForm.appBarClass,
          buttonBgColor:
            toHex(flat?.buttonBgColor) ??
            extractBgHex(flat?.buttonClass) ??
            extractBgHex(flat?.theme?.buttonClass) ??
            defaultFlowDesignForm.buttonBgColor,
          buttonTextColor:
            toHex(flat?.buttonTextColor) ??
            defaultFlowDesignForm.buttonTextColor,
          secondaryButtonTextColor:
            toHex(flat?.secondaryButtonTextColor) ??
            defaultFlowDesignForm.secondaryButtonTextColor,
          documentLinkBgColor:
            toHex(flat?.documentLinkBgColor) ??
            extractBgHex(flat?.documentLinkClass) ??
            defaultFlowDesignForm.documentLinkBgColor,
          allowedIdsColor:
            toHex(flat?.allowedIdsColor) ??
            toHex(flat?.documents?.[0]?.color) ??
            defaultFlowDesignForm.allowedIdsColor,
          allowedIdsTextColor:
            toHex(flat?.allowedIdsTextColor) ??
            toHex(flat?.documents?.[0]?.textColor) ??
            defaultFlowDesignForm.allowedIdsTextColor,
          appBarButtonColor:
            toHex(flat?.appBarButtonColor) ??
            toHex(flat?.appBar?.appBarButtonColor) ??
            defaultFlowDesignForm.appBarButtonColor,
          backgroundColor:
            toHex(flat?.backgroundColor) ??
            toHex(flat?.theme?.backgroundColor) ??
            defaultFlowDesignForm.backgroundColor,
          textColor:
            toHex(flat?.textColor) ??
            toHex(flat?.theme?.textColor) ??
            defaultFlowDesignForm.textColor,
          primaryTextColor:
            toHex(flat?.primaryTextColor) ??
            defaultFlowDesignForm.primaryTextColor,
          progressBarTrackColor:
            toHex(flat?.progressBarTrackColor ?? flat?.progressBarOuterColor) ??
            defaultFlowDesignForm.progressBarTrackColor,
          progressBarFillColor:
            toHex(flat?.progressBarFillColor ?? flat?.progressBarInnerColor) ??
            defaultFlowDesignForm.progressBarFillColor,
          translations: deepMergePlain(
            defaultFlowDesignForm.translations as unknown as Record<
              string,
              unknown
            >,
            rawTranslations,
          ) as FlowDesignTranslations,
          color: themeColor
            ? { ...defaultFlowDesignForm.color, ...themeColor }
            : defaultFlowDesignForm.color,
        };

        setFlowDesign(nextDesign);
        setStep(2);
      })
      .catch(err => {
        if (!cancelled) {
          setLoadFlowError(
            err?.response?.data?.message ??
              err?.response?.data?.error ??
              err?.message ??
              'Failed to load flow',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFlow(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editFlowId]);

  const canProceed =
    selected.length > 0 &&
    flowName.trim().length > 0 &&
    (!useCustomCustomer || customCustomerId.trim().length > 0);

  async function handleSave() {
    if (!canProceed || isSaving) return;
    setIsSaving(true);
    setSaveError(null);

    const name = flowName.trim();
    const checkOptions = buildCheckOptionsFromConfigs(selected, configs);
    const dataMatch = buildDataMatchPayload(selected, configs);
    const idvCfg = configs.idv ?? defaultCheckConfig('idv');
    const idvDocs = idvCfg.checkoptions
      .flatMap(o => o.documents)
      .filter(Boolean) || ['ID', 'PASSPORT'];

    const saveFlowDesign = async (targetFlowId: string) => {
      const formData = new FormData();
      appendFlowDesignFormData(formData, flowDesign, {
        checkOptions,
        documentTypes: [...new Set(idvDocs)],
        dataMatch,
      });
      await axios.put(
        `${API_BASE_URL}/flows?flowId=${encodeURIComponent(targetFlowId)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    };

    try {
      if (isEditMode) {
        await saveFlowDesign(editFlowId);
        navigate('/flows/flow-list');
        return;
      }

      const payload = {
        customerId,
        name,
        externalIncodeId: incodeClientId.trim(),
        checkOptions,
        dataMatch,
      };

      const response = await axios.post(`${API_BASE_URL}/flows`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const apiFlowId =
        (response?.data &&
          (response.data.id || response.data.flowId || response.data._id)) ||
        null;
      if (!apiFlowId) {
        throw new Error('Flow created but no flow ID returned from API');
      }

      await saveFlowDesign(String(apiFlowId));
      navigate('/flows/flow-list');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save flow';
      setSaveError(String(message));
    } finally {
      setIsSaving(false);
    }
  }

  function handleFlowDesignChange(
    updates: Partial<FlowDesignForm> & {
      translations?: FlowDesignTranslations;
    },
  ) {
    setFlowDesign(prev => {
      const { translations: ut, ...rest } = updates;
      const bannerRemoved =
        'bannerImg' in updates || 'bannerImageFile' in updates
          ? !rest.bannerImageFile && !rest.bannerImg
          : prev.bannerRemoved;
      return {
        ...prev,
        ...rest,
        bannerRemoved,
        ...(ut
          ? {
              translations: deepMergePlain(
                prev.translations as unknown as Record<string, unknown>,
                ut as unknown as Record<string, unknown>,
              ) as FlowDesignTranslations,
            }
          : {}),
      };
    });
  }

  const selectedProducts = useMemo(
    () => selected.map(id => PRODUCTS.find(p => p.id === id)!).filter(Boolean),
    [selected],
  );

  const previewDocumentTypes = useMemo(() => {
    if (selected.includes('poa') && !selected.includes('idv')) {
      const docs =
        (configs.poa ?? defaultCheckConfig('poa')).checkoptions.flatMap(
          o => o.documents,
        ) ?? [];
      return {
        invoice: docs.includes('INVOICE'),
        bill: docs.includes('BILL'),
        bank: docs.includes('BANK'),
      };
    }
    const docs =
      (configs.idv ?? defaultCheckConfig('idv')).checkoptions.flatMap(
        o => o.documents,
      ) ?? [];
    return {
      id: docs.includes('ID'),
      passport: docs.includes('PASSPORT'),
      residence: docs.includes('RESIDENCE'),
      drivingLicense: docs.includes('DRIVINGLICENSE'),
    };
  }, [selected, configs]);

  function configFor(id: ProductId): CheckConfig {
    return configs[id] ?? defaultCheckConfig(id);
  }

  function toggle(id: ProductId) {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(p => p !== id);
      // Credit Card Verification is exclusive — cannot combine with other products
      if (id === 'card') return ['card'];
      return [...prev.filter(p => p !== 'card'), id];
    });
    setExpanded(id);
  }

  function move(id: ProductId, dir: -1 | 1) {
    setSelected(prev => {
      const i = prev.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  if (loadingFlow) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-50 text-muted-foreground">
        Loading flow…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 text-foreground">
      <nav className="sticky top-0 z-50 border-b border-[#E4EBF4] bg-white/95 text-foreground backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-lg font-extrabold tracking-[-0.02em] text-[#0C2340]">
                SONIO Orchestrator
              </span>
            </div>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-1 text-sm">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => navigate('/flows/flow-list')}
              >
                Flows /
              </button>
              <span className="font-medium">
                {flowName.trim()
                  ? flowName
                  : isEditMode
                    ? 'Edit Flow'
                    : 'New KYC Flow'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/flows/flow-list')}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canProceed || isSaving}
              onClick={handleSave}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving
                ? 'Saving…'
                : isEditMode
                  ? 'Save Changes'
                  : 'Save Configuration'}
            </button>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-6xl px-6 py-12">
        {(loadFlowError || saveError) && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadFlowError || saveError}
          </div>
        )}

        <div className="mb-12 flex flex-wrap items-center justify-center gap-4">
          {STEPS.map((s, i) => (
            <div key={s.n} className="flex items-center gap-4">
              {i > 0 && <div className="h-px w-10 bg-border" />}
              <button
                type="button"
                onClick={() => (s.n === 1 || selected.length) && setStep(s.n)}
                disabled={s.n > 1 && selected.length === 0}
                className={`flex items-center gap-2 transition-opacity ${
                  step === s.n ? '' : 'opacity-40 hover:opacity-70'
                } disabled:cursor-not-allowed`}
              >
                <span
                  className={`flex size-7 items-center justify-center rounded-full text-[12px] font-bold ${
                    step === s.n
                      ? ' bg-white text-[#0C2340]'
                      : 'bg-white text-[#5B6B80]'
                  }`}
                >
                  {step > s.n ? <Check className="size-3.5" /> : s.n}
                </span>
                <span className="font-semibold">{s.label}</span>
              </button>
            </div>
          ))}
        </div>

        {step === 1 && (
          <StepSelect
            flowName={flowName}
            setFlowName={setFlowName}
            incodeId={incodeClientId}
            setIncodeId={setIncodeClientId}
            apiVersion={apiVersion}
            setApiVersion={setApiVersion}
            googleAddress={googleAddress}
            setGoogleAddress={setGoogleAddress}
            notification={notification}
            setNotification={setNotification}
            useCustomCustomer={useCustomCustomer}
            setUseCustomCustomer={setUseCustomCustomer}
            customCustomerId={customCustomerId}
            setCustomCustomerId={setCustomCustomerId}
            selected={selected}
            toggle={toggle}
            onContinue={() => setStep(2)}
            selectedProducts={selectedProducts}
          />
        )}

        {step === 2 && (
          <StepConfigure
            selectedProducts={selectedProducts}
            expanded={expanded}
            setExpanded={setExpanded}
            move={move}
            remove={id => toggle(id)}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            configFor={configFor}
            setConfig={(id, next) =>
              setConfigs(prev => ({ ...prev, [id]: next }))
            }
          />
        )}

        {step === 3 && (
          <div className="space-y-6">
            <section className="rounded-[14px] border-[1.5px] border-[#E5E7EB] bg-white p-8">
              <div className="mb-8">
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
                  Step 3
                </span>
                <h2 className="mt-1 flex items-center gap-2 text-2xl font-bold">
                  <Palette className="size-5 text-primary" />
                  UI customization
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                  Branding uses the working Sonio phone preview and color
                  controls. Changes here are saved with the existing flow design
                  payload.
                </p>
              </div>
              <FlowCustomContent
                design={flowDesign}
                previewDocumentTypes={previewDocumentTypes}
                onDesignChange={handleFlowDesignChange}
              />
            </section>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-white/60"
              >
                <ChevronLeft className="size-4" />
                Back to rules
              </button>
              <button
                type="button"
                disabled={!canProceed || isSaving}
                onClick={handleSave}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving
                  ? 'Saving…'
                  : isEditMode
                    ? 'Save changes'
                    : 'Save & publish flow'}
                <ArrowRight className="size-4" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StepSelect({
  flowName,
  setFlowName,
  incodeId,
  setIncodeId,
  apiVersion,
  setApiVersion,
  googleAddress,
  setGoogleAddress,
  notification,
  setNotification,
  useCustomCustomer,
  setUseCustomCustomer,
  customCustomerId,
  setCustomCustomerId,
  selected,
  toggle,
  onContinue,
  selectedProducts,
}: {
  flowName: string;
  setFlowName: (v: string) => void;
  incodeId: string;
  setIncodeId: (v: string) => void;
  apiVersion: string;
  setApiVersion: (v: string) => void;
  googleAddress: boolean;
  setGoogleAddress: (v: boolean) => void;
  notification: NotificationConfig;
  setNotification: (v: NotificationConfig) => void;
  useCustomCustomer: boolean;
  setUseCustomCustomer: (v: boolean) => void;
  customCustomerId: string;
  setCustomCustomerId: (v: string) => void;
  selected: ProductId[];
  toggle: (id: ProductId) => void;
  onContinue: () => void;
  selectedProducts: Product[];
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const input =
    'mt-1.5 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20';

  return (
    <>
      <section className="mb-10">
        <div className="mb-8 text-center">
          <h1 className="text-[32px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
            Build your verification stack
          </h1>
          <p className="mt-2 text-muted-foreground">
            Pick the products you want to run in this orchestration flow.
          </p>
        </div>

        <div className="mx-auto mb-8 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              Flow name
            </label>
            <input
              value={flowName}
              onChange={e => setFlowName(e.target.value)}
              placeholder="e.g. Novibet - POA"
              className={input}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              Customer
            </label>
            {useCustomCustomer ? (
              <input
                value={customCustomerId}
                onChange={e => setCustomCustomerId(e.target.value)}
                placeholder="Customer ID"
                className={`${input} font-mono`}
              />
            ) : (
              <div
                className={`${input} flex items-center text-muted-foreground`}
              >
                SONIO Demo
              </div>
            )}
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={useCustomCustomer}
                onChange={e => setUseCustomCustomer(e.target.checked)}
              />
              Use a different customer ID
            </label>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              Incode Client ID
            </label>
            <input
              value={incodeId}
              onChange={e => setIncodeId(e.target.value)}
              placeholder="e.g. 6555de3e91df98911fb301ea"
              className={`${input} font-mono`}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              API version
            </label>
            <select
              value={apiVersion}
              onChange={e => setApiVersion(e.target.value)}
              className={input}
            >
              <option value="3">3</option>
              <option value="4">4</option>
            </select>
          </div>
        </div>

        {selected.includes('card') && (
          <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-primary/20 bg-accent px-4 py-3 text-sm text-primary">
            <Lock className="mt-0.5 size-4 shrink-0" />
            <span>
              Credit Card Verification runs on its own — the other products are
              unavailable while it is selected.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {PRODUCTS.map(p => {
            const isSelected = selected.includes(p.id);
            const cardExclusiveActive = selected.includes('card');
            const otherProductsActive = selected.some(id => id !== 'card');
            // While Card is selected, other products are unavailable.
            // Card itself stays clickable so it can be deselected.
            const isUnavailable =
              !isSelected && p.id !== 'card' && cardExclusiveActive;
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                type="button"
                disabled={isUnavailable}
                onClick={() => toggle(p.id)}
                title={
                  isUnavailable
                    ? 'Deselect Credit Card Verification to combine products'
                    : p.id === 'card' && otherProductsActive && !isSelected
                      ? 'Credit Card Verification replaces other products'
                      : undefined
                }
                className={`group relative flex flex-col rounded-[14px] bg-white p-6 text-left transition-all duration-300 ${
                  isSelected
                    ? 'border-[1.5px] border-[#00387D] shadow-[0_0_0_3px_rgba(0,56,125,0.08)]'
                    : isUnavailable
                      ? 'cursor-not-allowed border-[1.5px] border-[#E5E7EB] opacity-40'
                      : 'border-[1.5px] border-[#E5E7EB] hover:border-[#00387D] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,56,125,0.10)]'
                }`}
              >
                <div className="mb-4 flex items-center justify-between">
                  <div
                    className={`transition-colors ${
                      isSelected ? 'text-[#00387D]' : 'text-[#5B6B80]'
                    }`}
                  >
                    <Icon className="size-[26px]" strokeWidth={1.75} />
                  </div>
                  <div
                    className={`flex size-5 items-center justify-center rounded-full transition-colors ${
                      isSelected
                        ? 'bg-primary'
                        : 'border border-border bg-white'
                    }`}
                  >
                    {isSelected && (
                      <Check className="size-3 text-white" strokeWidth={3} />
                    )}
                  </div>
                </div>
                <h3
                  className={`text-[16.5px] font-extrabold tracking-[-0.01em] text-[#0C2340]`}
                >
                  {p.name}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {p.description}
                </p>
                <div className="mt-4 flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
                  <span className="rounded bg-surface-100 px-1.5 py-0.5">
                    {p.short}
                  </span>
                  <span>{p.duration} for user</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 overflow-hidden rounded-[14px] border-[1.5px] border-[#E5E7EB] bg-white">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex w-full items-center justify-between px-5 py-4 text-left"
          >
            <div className="flex items-center gap-2">
              <Settings2 className="size-4 text-primary" />
              <div>
                <div className="text-sm font-bold">Flow settings & webhook</div>
                <div className="text-[11px] text-muted-foreground">
                  Notification endpoint, auth and flow-level flags
                </div>
              </div>
            </div>
            {showAdvanced ? (
              <ChevronDown className="size-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-4 text-muted-foreground" />
            )}
          </button>
          {showAdvanced && (
            <div className="space-y-5 border-t border-border bg-surface-50 p-5">
              <Panel
                title="Notification webhook"
                description="Where check results are pushed."
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field
                    label="URL"
                    hint="Use “skip” to disable notifications."
                  >
                    <input
                      value={notification.url}
                      onChange={e =>
                        setNotification({
                          ...notification,
                          url: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </Field>
                  <Field label="Auth type">
                    <Select
                      value={notification.authtype}
                      onChange={v =>
                        setNotification({ ...notification, authtype: v })
                      }
                      options={['basic', 'bearer', 'hmac', 'none']}
                    />
                  </Field>
                  <Field label="Username">
                    <input
                      value={notification.username}
                      onChange={e =>
                        setNotification({
                          ...notification,
                          username: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </Field>
                  <Field label="Password">
                    <input
                      type="password"
                      value={notification.password}
                      onChange={e =>
                        setNotification({
                          ...notification,
                          password: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </Field>
                  <ToggleRow
                    label="Send 400 responses"
                    hint="Notify on rejected payloads"
                    on={notification.send400}
                    onChange={v =>
                      setNotification({ ...notification, send400: v })
                    }
                  />
                  <ToggleRow
                    label="Retry on 500"
                    hint="Retry when your endpoint errors"
                    on={notification.retryon500}
                    onChange={v =>
                      setNotification({ ...notification, retryon500: v })
                    }
                  />
                </div>
              </Panel>
              <Panel
                title="Flow flags"
                description="Behaviour applied to every check in the flow."
              >
                <ToggleRow
                  mono
                  label="use_google_address_validation"
                  hint="Normalise addresses with Google before matching"
                  on={googleAddress}
                  onChange={setGoogleAddress}
                />
              </Panel>
            </div>
          )}
        </div>
      </section>

      <div className="sticky bottom-6 mt-8 rounded-[14px] border-[1.5px] border-[#E5E7EB] bg-white/95 backdrop-blur p-4 shadow-[0_12px_30px_rgba(0,56,125,0.10)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              In this flow
            </span>
            {selectedProducts.length === 0 ? (
              <span className="text-sm text-muted-foreground">
                Nothing selected yet
              </span>
            ) : (
              selectedProducts.map((p, i) => (
                <span
                  key={p.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-primary"
                >
                  <span className="text-[10px] font-bold opacity-60">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {p.short}
                </span>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={onContinue}
            disabled={selectedProducts.length === 0 || !flowName.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            Configure {selectedProducts.length || ''} product
            {selectedProducts.length === 1 ? '' : 's'}
            <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
    </>
  );
}

function StepConfigure({
  selectedProducts,
  expanded,
  setExpanded,
  move,
  remove,
  onBack,
  onNext,
  configFor,
  setConfig,
}: {
  selectedProducts: Product[];
  expanded: ProductId | null;
  setExpanded: (v: ProductId | null) => void;
  move: (id: ProductId, dir: -1 | 1) => void;
  remove: (id: ProductId) => void;
  onBack: () => void;
  onNext: () => void;
  configFor: (id: ProductId) => CheckConfig;
  setConfig: (id: ProductId, next: CheckConfig) => void;
}) {
  return (
    <section className="rounded-[14px] border-[1.5px] border-[#E5E7EB] bg-white p-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
            Step 2
          </span>
          <h2 className="mt-1 text-2xl font-bold">Rules & validation</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Every check exposes its provider routing, data-match thresholds,
            validation rules, decisioning and feature flags. Reorder to change
            the user journey.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-surface-50"
        >
          <ChevronLeft className="size-3.5" />
          Edit selection
        </button>
      </div>

      <div className="space-y-3">
        {selectedProducts.map((p, idx) => {
          const isOpen = expanded === p.id;
          const Icon = p.icon;
          const cfg = configFor(p.id);
          const activeMatches = Object.values(cfg.settings.datamatch).filter(
            r => r.active,
          ).length;
          const activeValidations = Object.values(
            cfg.settings.validation,
          ).filter(v => v.active).length;
          return (
            <div
              key={p.id}
              className={`overflow-hidden rounded-xl border transition-colors ${
                isOpen ? 'border-primary/40' : 'border-border'
              }`}
            >
              <div
                className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
                  isOpen ? 'bg-accent/50' : 'bg-surface-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="cursor-grab text-muted-foreground/60 hover:text-muted-foreground"
                    title="Move up"
                    onClick={() => move(p.id, -1)}
                  >
                    <GripVertical className="size-4" />
                  </button>
                  <span className="w-6 text-[10px] font-bold text-muted-foreground">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="text-[#00387D]">
                    <Icon className="size-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {cfg.provider} · {activeMatches} match rules ·{' '}
                      {activeValidations} validations · retry{' '}
                      {cfg.settings.retryonfail}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    <span className="size-1.5 rounded-full bg-success" />
                    Ready
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-white hover:text-destructive"
                    title="Remove from flow"
                  >
                    <X className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : p.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-white"
                  >
                    {isOpen ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-border bg-white p-6">
                  <ProductConfig
                    id={p.id}
                    config={cfg}
                    onChange={next => setConfig(p.id, next)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-surface-50"
        >
          <ChevronLeft className="size-4" />
          Back
        </button>
        <div className="flex items-center gap-4">
          <div className="text-xs text-muted-foreground">
            <span className="font-bold text-foreground">
              {selectedProducts.length}
            </span>{' '}
            products ·{' '}
            <span className="font-bold text-foreground">
              ~
              {selectedProducts.reduce(
                (acc, p) => acc + parseInt(p.duration.replace(/\D/g, ''), 10),
                0,
              )}
              s
            </span>{' '}
            estimated user time
          </div>
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary-dark"
          >
            <Palette className="size-4" />
            Continue to UI customization
            <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
